/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import reportInvoiceService from 'service/reportInvoice';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { fieldMappingReturn } from 'data/fieldMaping';

export type ReturnDataType = {
  product_name: string;
  barcode: string;
  currency: string;
  unit: string;
  category: string;
  cost: string;
  quantity: string;
  sum: string;
  serial_number: any;
};

interface ReturnTableProps {
  generateTableData?: any;
  setGeneratedTableData?: any;
  getName?: any;
  getBarcode?: any;
  getFilterData?: any;
  savedValues?: any;
  setTotalAmount?: any;
  currentPage?: any;
  setCurrentPage?: any;
  pageCount?: any;
  setPageCount?: any;
  debouncedBarcode?: any;
  setLabeledVisibleFields?: any;
}

const ReturnTable = ({
  generateTableData,
  setGeneratedTableData,
  getName,
  getBarcode,
  getFilterData,
  savedValues,
  setTotalAmount,
  currentPage,
  setCurrentPage,
  pageCount,
  setPageCount,
  debouncedBarcode,
  setLabeledVisibleFields
}: ReturnTableProps) => {
  const { t, i18n } = useTranslation();
  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_pid
  );

  const [hasReset, setHasReset] = useState(false);

  const branchId = useSelector((state: any) => state?.auth?.legal_entity?.pid);
  const branchName = useSelector(
    (state: any) => state?.auth?.legal_entity?.name
  );

  // Memoized onVisibleFieldsChange callback with shallow comparison
  const handleVisibleFieldsChange = useCallback(
    (visibleFields: { [key: string]: { hidden: boolean; order: number } }) => {
      const newLabeledFields = Object.keys(visibleFields)
        .filter(columnKey => !visibleFields[columnKey].hidden) // Only include non-hidden fields
        .map(columnKey => {
          const backendField = Object.keys(fieldMappingReturn).find(
            key => fieldMappingReturn[key].columnKey === columnKey
          );
          return backendField || null;
        })
        .filter((field): field is string => field !== null) // Filter out null values
        .join(','); // Join into a comma-separated string

      // Only update state if newLabeledFields is different
      setLabeledVisibleFields((prev: any) => {
        const isEqual = prev === newLabeledFields;
        if (!isEqual) {
          return newLabeledFields;
        }
        return prev;
      });
    },
    [fieldMappingReturn]
  );

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_REPORTS_REMAINING', getName, debouncedBarcode, currentPage],
    async () => {
      pageToOffset(currentPage, 10);

      const res = await reportInvoiceService
        .getReportReturnList({
          limit: 10,
          offset: pageToOffset(currentPage, 10),
          name: getName,
          barcode: debouncedBarcode,
          currency_pid: Object.keys(getFilterData).length === 0 && currencyId,
          branch_pid: Object.keys(getFilterData).length === 0 && branchId,
          is_group: savedValues?.length === 0 ? true : undefined,
          ...getFilterData
        })
        .then((res: any) => {
          setTotalAmount(res?.total_amount);
          setPageCount(Math.ceil(res?.count / 10));

          return res?.list;
        });
      return res;
    },
    {
      enabled:
        getName.length === 0 ||
        getName.length > 2 ||
        debouncedBarcode.length === 0 ||
        debouncedBarcode.length > 2 ||
        !!currencyId ||
        !!branchId ||
        !!getFilterData
    }
  );

  const loading = isLoading || isFetching;

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      setGeneratedTableData([]); // Set empty array if no data
      return;
    }

    if (data) {
      const resData =
        data?.map((items: any) => {
          let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

          timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

          return {
            product_name: items?.product?.name,
            barcode: items?.product?.barcode,
            unit: items?.product?.nomenclature?.measure_unit?.name?.[
              i18n?.language
            ],
            currency: items?.currency?.name?.[i18n?.language],
            measure_unit_id: items?.items?.measure_unit_id,
            supplier: items?.legal_entity?.name || '',
            warehouse_name: items?.product?.ware_house?.name || '',
            branch: branchName || '',
            cost_in: items?.price_in
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(items?.price_in)
                  .replace(/,/g, ' ')
              : 0,

            quantity: items?.income_amount
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(items?.income_amount)
                  .replace(/,/g, ' ')
              : 0,
            create_date: formatDateToTimeZone(items?.date_create, timeZone)
          };
        }) ?? [];

      if (resData) {
        setGeneratedTableData(resData);
      }
    }
  }, [data, setGeneratedTableData, t, i18n?.language]);

  const remainderDataTableColumns: ColumnDef<ReturnDataType>[] = [
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10; // Calculate the order number based on the row index
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'cost_in',
      header: t('short_price_in'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'warehouse_name',
      header: t('warehouse'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'branch',
      header: t('branch'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'supplier',
      header: t('supplier'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'create_date',
      header: t('date_create'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateTableData,
    columns: remainderDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if ((getName || getBarcode) && !hasReset) {
      setCurrentPage(1);

      setHasReset(true); // Prevents multiple resets
    }
  }, [getName, getBarcode, hasReset]);

  useEffect(() => {
    // Ensure currentPage is updated
    refetch();
  }, [getName, debouncedBarcode, currentPage]);

  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          isLoading={loading}
          onVisibleFieldsChange={handleVisibleFieldsChange}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </AdvanceTableProvider>
  );
};

export default ReturnTable;
